import { useEffect, useId, useState } from "react";

import { ChipToggle, TradeIcon } from "@ui/react";
import { AlertIcon } from "@ui/react/icons";
import { listTrades, type Trade } from "../lib/api/contractor";
import { LAUNCH_TRADES, jobCategoryDisplay } from "../lib/types/enums";

/**
 * Trade-category multi-select.
 *
 * The stored value is the enum *name* ("hvac"), not the label ("HVAC"): the
 * backend matches a job's `category` against these strings verbatim, so a
 * label leaking into `skills` silently removes the contractor from that trade's
 * feed with no error anywhere.
 *
 * `role="group"` plus `aria-labelledby`/`aria-describedby` reproduce what
 * `<Field>` does for a real input — the chips are buttons, so there is no
 * control for a `<label>` to point at.
 */
export function SkillsPicker({
  value,
  onChange,
  error,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  error?: string | null;
}) {
  const labelId = useId();
  const hintId = useId();
  const errorId = useId();

  // Bundled LAUNCH_TRADES renders immediately — ProfileCompletionScreen blocks
  // submit while nothing is selected, so an empty offered list would block
  // onboarding. `listTrades` never rejects, so this either stays the bundled
  // four or swaps for a non-empty `GET /categories` list; no loading/error UI.
  const [trades, setTrades] = useState<Trade[]>(() =>
    LAUNCH_TRADES.map((trade) => ({ value: trade, label: jobCategoryDisplay(trade) })),
  );
  useEffect(() => {
    void listTrades().then(setTrades);
  }, []);

  const toggle = (category: string) => {
    onChange(
      value.includes(category)
        ? value.filter((existing) => existing !== category)
        : [...value, category],
    );
  };

  // A held value the offered list doesn't cover (an old trade, or one the
  // remote config dropped) still needs a real label, not a blank chip —
  // remote label first, then the bundled table, then the raw value.
  const labelFor = (category: string) =>
    trades.find((trade) => trade.value === category)?.label ?? jobCategoryDisplay(category);

  // The remote glyph, for a trade this build bundles no drawing for. Unlike
  // `labelFor` there is no fallback chain here: TradeIcon prefers its OWN
  // bundled drawing over this and ends at the toolbox, so a missing icon and a
  // refused one land in the same place.
  const iconFor = (category: string) =>
    trades.find((trade) => trade.value === category)?.icon;

  return (
    <div
      className="field"
      role="group"
      aria-labelledby={labelId}
      aria-describedby={error ? `${hintId} ${errorId}` : hintId}
    >
      <span className="field__label" id={labelId}>
        Trade categories <span aria-hidden="true">*</span>
        <span className="sr-only">(required)</span>
      </span>

      <div className="chip-row">
        {Array.from(new Set([...trades.map((trade) => trade.value), ...value])).map((category) => (
          <ChipToggle
            key={category}
            pressed={value.includes(category)}
            onToggle={() => toggle(category)}
          >
            <TradeIcon trade={category} pathBody={iconFor(category)} />
            {labelFor(category)}
          </ChipToggle>
        ))}
      </div>

      <span className="field__hint" id={hintId}>
        Pick every trade you take work in. Homeowners only see you for the categories you select.
      </span>

      {error ? (
        <span className="field__error" id={errorId}>
          <AlertIcon />
          {error}
        </span>
      ) : null}
    </div>
  );
}
